import { Component, OnInit } from '@angular/core';
import { ILecture } from 'src/app/views/pages/lectures/lecture.interface';
import { CitiesService } from 'src/app/views/pages/lectures/cities/cities.service';
import { FirestoreRulesService } from 'src/app/core/firestore-rules/firestore-rules.service';
import { LanguagesEnum, LectureStatusEnum, LectureValuesData } from 'src/app/submit-lecture/lecture.values';
import { LecturesService } from 'src/app/views/pages/lectures/lectures.service';
import { UserService } from 'src/app/users/services/user.service';
import { COMPLETION_SCORE } from 'src/app/users/services/user-score.service';
import { IUser } from 'src/app/users/interfaces/user.interface';

const ALL_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE = 'ALL_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE';
export const MIN_CACHE_ROWS_2_TRIGGER_REMOTE_FETCHING = 2;
const USERS_LAST_USER_CHANGED_UPDATE_DATE = 'USERS_LAST_USER_CHANGED_UPDATE_DATE';

@Component({
  selector: 'migrate-users-progress',
  templateUrl: './migrate-users-progress.component.html',
  styleUrls: ['./migrate-users-progress.component.scss']
})

export class MigrateUsersProgressComponent implements OnInit {

  migratingArr: ILecture[] = [];
  usersTransformArr: IUser[] = [];
  failedUsersTransformArr: IUser[] = [];

  lectures: any;
  usersMeta: any = [];
  defaultUser: string;
  defaultCity: string;
  isFinished: boolean;

  constructor(private citiesService: CitiesService,
              private firestoreRulesService: FirestoreRulesService,
              private lecturesService: LecturesService,
              private userService: UserService) {
  }

  ngOnInit(): void {
    this.getAllUsersMeta().then((res) => {
      this.usersMeta = res;
      // this.usersMeta = new Array(res);
      this.modifyUsersMetaLogic();
    });
  }


  modifyLecturesLogic(): void {
    this.lectures.forEach(lecture => {
      const origLecture = {...lecture};
      const cityId = lecture.address.city;
      const cityInfo = this.citiesService.getCityById(cityId);
      lecture.address.subdistrict = cityInfo.subdistrict_id;
      this.lecturesService.updateLecture(lecture, origLecture);
    });
  }

  private modifyUsersMetaLogic(): void {
    this.usersMeta.forEach(async meta => {
      // Get closed lectures by user
      const docs = await this.lecturesService.getLecturesByUserIdAndStatus(meta.metadata.docId, LectureStatusEnum.COMPLETE) as ILecture[];
      let numOfCompleted = 0;
      let numOfParticipants = 0;
      console.log('Closed lectures for ' + meta.email, docs);
      docs.forEach(d => {
        if (d.feedback) {
          numOfCompleted += Number(d.feedback?.numberOfLectures);
          numOfParticipants += Number(d.feedback?.numberOfAttendees);
        }
      });

      if (!meta.progress) {
        meta.progress = {numOfCompleted: 0, numOfParticipants: 0, totalScore: 0};
      } else {
        if (meta.progress?.numberOfLectures) {
          meta.progress.numOfCompleted = meta.progress?.numberOfLectures;
          delete meta.progress?.numberOfLectures;
        }
      }

      meta.progress.numOfCompleted = numOfCompleted;
      meta.progress.numOfParticipants = numOfParticipants;
      meta.progress.totalScore = numOfCompleted * COMPLETION_SCORE;

      meta.docId = meta.metadata.docId;
      console.log(meta);
    });
  }

  private runUpdate(): void {
    this.usersMeta.forEach(async meta => {
      try {
        this.userService.updateUserMetadataByDocId(meta.metadata.docId, meta);
        this.usersTransformArr.push(meta);
        console.log('Meta Updated: ', meta);
      } catch (err) {
        this.failedUsersTransformArr.push(meta);
        console.log('Meta FAILED: ', meta);
      }
    });
    this.isFinished = true;
  }

  start(): void {
    this.runUpdate();
  }

  private getStatus(status: string): LectureStatusEnum {
    const filtered: any[] = LectureValuesData.statuses.filter(st => {
      return (status.includes(st.nameEN) || status.includes(st.nameHE));
    });
    return filtered[0]?.id;
  }

  private getLanguage(lang: string): LanguagesEnum {
    const filtered: any[] = LectureValuesData.languages.filter(st => {
      return (lang.includes(st.nameEN) || lang.includes(st.nameHE));
    });
    return filtered[0].id;
  }

  getAllLectures(query?: any, forceCache = false): Promise<ILecture[]> {
    let lastChangedDocs;

    return  new Promise<ILecture[]>(async (resolve, reject) => {
        try {
          const lastLectureUpdateFromLocalStorage = localStorage.getItem(ALL_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE);
          let docs = await this.firestoreRulesService.getAllLectures(true, query);
          // check if any lecture was updated since last cached
          if (docs && docs.length) {
            lastChangedDocs = await this.firestoreRulesService.getLastChangedLecture();
          }
          // if data was not fetched from cache or changes exist on fb lectures data then get data from fb
          if (this.isRemoteDataFetching(forceCache, docs, lastLectureUpdateFromLocalStorage, lastChangedDocs) ||
            docs?.length <= MIN_CACHE_ROWS_2_TRIGGER_REMOTE_FETCHING) {
            docs = await this.firestoreRulesService.getAllLectures(false, query);
            if (lastChangedDocs && lastChangedDocs.length > 0) {
              const changedDate = this.convertToDate(lastChangedDocs[0]?.updated_at);
              if (changedDate) {
                localStorage.setItem(ALL_LECTURES_LAST_LECTURE_CHANGED_UPDATE_DATE, changedDate.toString());
              }
            }
          }
          resolve(docs);
        } catch (error) {
          reject(error);
        }
      });
  }

  private isRemoteDataFetching = (forceCache: boolean, docs: any[], lastLectureUpdateFromLocalStorage: string,
                                  lastChangedDocs: ILecture[]): boolean => {
    let result;
    result  = !forceCache && (!docs || docs.length === 0 || !lastLectureUpdateFromLocalStorage || (lastChangedDocs && lastChangedDocs.length
              && this.isLastLectureUpdateGreaterThanLocalStotageDate(lastChangedDocs, lastLectureUpdateFromLocalStorage)));
    return result;
  }

  private isLastLectureUpdateGreaterThanLocalStotageDate(lastChangedDocs: ILecture[], lastLectureUpdateFromLocalStorage: string): boolean {
    const parsedDate = new Date(lastLectureUpdateFromLocalStorage);
    const now = Date.now();
    const twoHours = 2 * 60 * 60 * 1000;
    const isMoreThanTwoHours = (now - parsedDate.getTime()) > twoHours;
    const lastChangedDocDate = this.convertToDate(lastChangedDocs[0].updated_at);
    return (lastChangedDocDate > parsedDate) || (isMoreThanTwoHours);
  }

  private getAllUsersMeta(): Promise<unknown> {
    let lastChangedDocs;

    return new Promise<unknown>(async (resolve, reject) => {
      try {
        const lastUserUpdateDate = localStorage.getItem(USERS_LAST_USER_CHANGED_UPDATE_DATE);
        let docs = await this.firestoreRulesService.getAllUsersMeta(false);
        // let docs = await this.getUserMetadata('I3dYgGRz9LNacmOwCBR0s8qqhUs1');
        // check if any lecture was updated since last cached
        if (docs && docs.length) {
          lastChangedDocs = await this.firestoreRulesService.getLastChangedUser();
        }
        // if data was not fetched from cache or changes exist on fb lectures data then get data from fb
        if (!docs || docs.length === 0 || !lastUserUpdateDate || (lastChangedDocs && lastChangedDocs.length
            && this.checkIfLastLectureUpdateIsGreaterThenLocalDate(lastChangedDocs, lastUserUpdateDate))) {
          // docs = await this.getUserMetadata('I3dYgGRz9LNacmOwCBR0s8qqhUs1');
          docs = await this.firestoreRulesService.getAllUsersMeta(false);
          if (lastChangedDocs) {
            localStorage.setItem(USERS_LAST_USER_CHANGED_UPDATE_DATE,
                this.convertToDate(lastChangedDocs[0]?.updated_at).toString());
          }
        }
        resolve(docs);
      } catch (error) {
        reject(error);
      }
    });
  }

  getUserMetadata(userId: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
        const data = this.firestoreRulesService.getUserMetadata(userId);
        data.subscribe((meta) => {
          resolve(meta);
        }, err => {
          console.log(err);
          reject(err);
        });
    });
  }

  private checkIfLastLectureUpdateIsGreaterThenLocalDate(lastChangedDocs: any[], lastUserUpdateDate: string): boolean {
    const parsedDate = new Date(lastUserUpdateDate);
    const lastChangedDocDate = this.convertToDate(lastChangedDocs[0].updated_at);
    return (lastChangedDocDate > parsedDate);
  }

  private convertToDate(d: any): Date {
    if (!d) {
      return;
    }
    if (d instanceof Date) {
      return d;
    }

    const seconds = (d as any).seconds;
    const date = new Date(seconds * 1000);
    return date;
  }
}
